'use client';

import TagBadge from '@/components/common/TagBadge';
import Image from 'next/image';
import { useParams } from 'next/navigation';
import { normalizeBlogPost } from '@/types/blogTypes';
import { useMemo, useEffect } from 'react';
import dynamic from 'next/dynamic';

const RecentBlogs = dynamic(() => import('@/components/common/RecentBlogs'), {
  ssr: false,
  loading: () => <div className="h-40 bg-gray-100 animate-pulse rounded-lg" />
});
import { useRecentBlogs } from '@/hooks/ApiHooks/blogHooks/getRecentBlogsHook';
import { useBlogStore } from '@/zustand/blogStore';
import { useQuery } from '@tanstack/react-query';
import { getCustomBlogBySlug } from '@/services/api/customBlogApi';
import Loader from '@/components/common/Loader';
import '@/app/blog.css';

// Utility function to keep only allowed blog classes and remove all others (including elementor)
const cleanHtmlClasses = (htmlContent: string): string => {
  if (!htmlContent) return '';

  // Whitelist of allowed classes
  const allowedClasses = [
    'blog-headline',
    'blog-intro-para',
    'blog-sub-headline',
    'blog-toc',
    'blog-contents',
    'blog-content',
    'blog-content-headlines',
    'blog-main-head',
    // WordPress and Code classes
    'wp-block-code',
    'language-javascript',
    'language-typescript',
    'language-css',
    'language-html',
    'language-php',
    'language-json',
    'language-bash',
    'language-sql',
    'language-python',
    'language-java',
    'language-c',
    'language-cpp',
    'language-go',
    'language-rust'
  ];

  let cleanedContent = htmlContent;

  // 0. Unescape literal backslash characters coming from the API/JSON
  // This fixes issues where \n and \t appear as text instead of whitespace
  cleanedContent = cleanedContent.replace(/\\n/g, '\n');
  cleanedContent = cleanedContent.replace(/\\t/g, '\t');
  cleanedContent = cleanedContent.replace(/\\"/g, '"');

  // 1. Strip all inline style attributes
  cleanedContent = cleanedContent.replace(/\s?style="[^"]*"/g, '');

  // 2. Replace &nbsp; with regular spaces to allow wrapping
  cleanedContent = cleanedContent.replace(/&nbsp;/g, ' ');

  // 3. Remove zero-width characters (ZWSP, ZWJ, ZWNJ, etc.)
  // These often appear in content copied from other editors and break layout
  cleanedContent = cleanedContent.replace(/[\u200B-\u200D\uFEFF]/g, '');

  // 4. Replace class attributes, keeping only whitelisted classes
  cleanedContent = cleanedContent.replace(/class="[^"]*"/g, (match) => {
    const classValue = match.slice(7, -1); // Remove 'class="' and '"'
    const classes = classValue.split(/\s+/);
    const filteredClasses = classes.filter(cls => allowedClasses.includes(cls));

    // Also allow classes starting with "language-" to be more flexible
    const additionalClasses = classes.filter(cls => cls.startsWith('language-') && !allowedClasses.includes(cls));

    const finalClasses = [...filteredClasses, ...additionalClasses];

    if (finalClasses.length === 0) {
      return ''; // Remove the entire class attribute if no allowed classes remain
    }

    return `class="${finalClasses.join(' ')}"`;
  });

  // Remove empty paragraphs
  cleanedContent = cleanedContent.replace(/<p[^>]*>\s*<\/p>/g, '');

  return cleanedContent.trim();
};

// Function to extract H1 and H2 headings from HTML content
const extractHeadings = (htmlContent: string): Array<{ id: string; text: string; level: number }> => {
  if (!htmlContent) return [];

  const headings: Array<{ id: string; text: string; level: number }> = [];

  // First try to extract h1 and h2 headings - more flexible regex
  const headingRegex = /<h([12])(?:\s+[^>]*)?>(.*?)<\/h[12]>/gi;
  let match;

  while ((match = headingRegex.exec(htmlContent)) !== null) {
    const level = parseInt(match[1]);
    let text = match[2];

    // Remove any nested HTML tags and decode HTML entities
    text = text.replace(/<[^>]*>/g, '');
    text = text.replace(/&nbsp;/g, ' ');
    text = text.replace(/&amp;/g, '&');
    text = text.replace(/&lt;/g, '<');
    text = text.replace(/&gt;/g, '>');
    text = text.replace(/&quot;/g, '"');
    text = text.replace(/&#39;/g, "'");
    text = text.replace(/&ZeroWidthSpace;/g, ''); // Remove zero-width space
    text = text.replace(/&#8203;/g, ''); // Remove zero-width space (numeric entity)
    text = text.replace(/&shy;/g, ''); // Remove soft hyphen
    text = text.replace(/&#173;/g, ''); // Remove soft hyphen (numeric entity)
    text = text.trim();

    // Skip empty headings
    if (!text) continue;

    const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

    headings.push({ id, text, level });
  }

  // If no h1/h2 headings found, try h3 as fallback
  if (headings.length === 0) {
    const h3Regex = /<h3(?:\s+[^>]*)?>(.*?)<\/h3>/gi;
    let h3Match;

    while ((h3Match = h3Regex.exec(htmlContent)) !== null) {
      let text = h3Match[1];

      // Remove any nested HTML tags and decode HTML entities
      text = text.replace(/<[^>]*>/g, '');
      text = text.replace(/&nbsp;/g, ' ');
      text = text.replace(/&amp;/g, '&');
      text = text.replace(/&lt;/g, '<');
      text = text.replace(/&gt;/g, '>');
      text = text.replace(/&quot;/g, '"');
      text = text.replace(/&#39;/g, "'");
      text = text.replace(/&ZeroWidthSpace;/g, ''); // Remove zero-width space
      text = text.replace(/&#8203;/g, ''); // Remove zero-width space (numeric entity)
      text = text.replace(/&shy;/g, ''); // Remove soft hyphen
      text = text.replace(/&#173;/g, ''); // Remove soft hyphen (numeric entity)
      text = text.trim();

      // Skip empty headings
      if (!text) continue;

      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

      headings.push({ id, text, level: 3 });
    }
  }

  return headings;
};

// Function to add IDs to headings in HTML content
const addIdsToHeadings = (htmlContent: string): string => {
  if (!htmlContent) return '';

  // First handle h1 and h2 tags
  let processedContent = htmlContent.replace(/<h([12])([^>]*)>/gi, (match, level, attributes) => {
    // Extract text content for ID generation
    const textMatch = match.match(/<h[12][^>]*>(.*?)<\/h[12]>/i);
    if (textMatch) {
      let text = textMatch[1];

      // Remove any nested HTML tags and decode HTML entities
      text = text.replace(/<[^>]*>/g, '');
      text = text.replace(/&nbsp;/g, ' ');
      text = text.replace(/&amp;/g, '&');
      text = text.replace(/&lt;/g, '<');
      text = text.replace(/&gt;/g, '>');
      text = text.replace(/&quot;/g, '"');
      text = text.replace(/&#39;/g, "'");
      text = text.replace(/&ZeroWidthSpace;/g, ''); // Remove zero-width space
      text = text.replace(/&#8203;/g, ''); // Remove zero-width space (numeric entity)
      text = text.replace(/&shy;/g, ''); // Remove soft hyphen
      text = text.replace(/&#173;/g, ''); // Remove soft hyphen (numeric entity)
      text = text.trim();

      if (!text) return match; // Skip empty headings

      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

      // Check if id attribute already exists
      if (attributes.includes('id=')) {
        return match; // Already has an ID
      }

      return `<h${level}${attributes} id="${id}">`;
    }
    return match;
  });

  // If no h1/h2 tags were found, also handle h3 tags
  const hasH1H2 = /<h[12][^>]*>/i.test(processedContent);
  if (!hasH1H2) {
    processedContent = processedContent.replace(/<h3([^>]*)>/gi, (match, attributes) => {
      // Extract text content for ID generation
      const textMatch = match.match(/<h3[^>]*>(.*?)<\/h3>/i);
      if (textMatch) {
        let text = textMatch[1];

        // Remove any nested HTML tags and decode HTML entities
        text = text.replace(/<[^>]*>/g, '');
        text = text.replace(/&nbsp;/g, ' ');
        text = text.replace(/&amp;/g, '&');
        text = text.replace(/&lt;/g, '<');
        text = text.replace(/&gt;/g, '>');
        text = text.replace(/&quot;/g, '"');
        text = text.replace(/&#39;/g, "'");
        text = text.trim();

        if (!text) return match; // Skip empty headings

        const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

        // Check if id attribute already exists
        if (attributes.includes('id=')) {
          return match; // Already has an ID
        }

        return `<h3${attributes} id="${id}">`;
      }
      return match;
    });
  }

  return processedContent;
};

// Function to scroll to element smoothly
const scrollToElement = (elementId: string) => {


  // Try multiple ways to find the element
  let element = document.getElementById(elementId);

  if (!element) {
    // Try finding by partial text match
    const headings = document.querySelectorAll('h1, h2, h3');
    for (const heading of headings) {
      const text = heading.textContent?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      if (text === elementId) {
        element = heading as HTMLElement;
        break;
      }
    }
  }

  if (!element) {
    // Try finding by text content
    const headings = document.querySelectorAll('h1, h2, h3');
    for (const heading of headings) {
      const text = heading.textContent?.toLowerCase();
      const targetText = elementId.replace(/-/g, ' ');
      if (text?.includes(targetText)) {
        element = heading as HTMLElement;
        break;
      }
    }
  }

  if (element) {

    element.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  } else {

    // Fallback: scroll to the blog content area
    const blogContent = document.querySelector('.blog-contents');
    if (blogContent) {
      blogContent.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }
};

export default function BlogDetail() {
  const params = useParams();
  const slug = params.slug as string;
  const setBlogId = useBlogStore((state) => state.setBlogId);

  const { data: blogPost, isLoading, error } = useQuery({
    queryKey: ['blog', slug],
    queryFn: () => getCustomBlogBySlug(slug),
    enabled: !!slug
  });

  // Normalize API response (raw or WordPress format) to BlogPost
  const transformedBlogPost = useMemo(() => {
    if (!blogPost) return null;
    return normalizeBlogPost(blogPost);
  }, [blogPost]);

  // ID for store and recent blogs (raw API uses _id, WP uses id)
  const blogId = blogPost && ('_id' in blogPost ? blogPost._id : (blogPost as { id?: string | number }).id);

  useEffect(() => {
    if (blogId != null) setBlogId(String(blogId));
  }, [blogId, setBlogId]);

  const { data: recentBlogs } = useRecentBlogs(blogId != null ? String(blogId) : undefined, 3);

  // Extract headings from the blog content
  const headings = useMemo(() => {
    if (!transformedBlogPost?.content) return [];
    return extractHeadings(transformedBlogPost.content);
  }, [transformedBlogPost]);

  // Process content to add IDs to headings
  const processedContent = useMemo(() => {
    if (!transformedBlogPost?.content) return '';
    const cleanedContent = cleanHtmlClasses(transformedBlogPost.content);
    return addIdsToHeadings(cleanedContent);
  }, [transformedBlogPost?.content]);

  // Ensure IDs are added to headings after content is rendered
  useEffect(() => {
    if (processedContent) {
      // Add a small delay to ensure DOM is fully rendered
      setTimeout(() => {
        // Add IDs to any headings that don't have them
        const headings = document.querySelectorAll('h1, h2, h3');

        headings.forEach((heading) => {
          if (!heading.id) {
            const text = heading.textContent || '';
            const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
            heading.id = id;

          }
        });
      }, 100);
    }
  }, [processedContent]);

  // Set SEO metadata and document title
  useEffect(() => {
    if (transformedBlogPost?.seo) {
      const { meta_title, meta_description, keywords, canonical_url } = transformedBlogPost.seo;

      // Update title
      if (meta_title) {
        document.title = meta_title;
      } else if (transformedBlogPost.title) {
        // Strip out HTML tags for document title and decode basic entities if any
        const plainTitle = transformedBlogPost.title.replace(/<[^>]*>?/gm, '');
        document.title = plainTitle;
      }

      // Update meta description
      if (meta_description) {
        let metaDesc = document.querySelector('meta[name="description"]');
        if (!metaDesc) {
          metaDesc = document.createElement('meta');
          metaDesc.setAttribute('name', 'description');
          document.head.appendChild(metaDesc);
        }
        metaDesc.setAttribute('content', meta_description);
      }

      // Update keywords
      if (keywords && keywords.length > 0) {
        let metaKeywords = document.querySelector('meta[name="keywords"]');
        if (!metaKeywords) {
          metaKeywords = document.createElement('meta');
          metaKeywords.setAttribute('name', 'keywords');
          document.head.appendChild(metaKeywords);
        }
        metaKeywords.setAttribute('content', keywords.join(', '));
      }

      // Update canonical URL
      if (canonical_url) {
        let linkCanonical = document.querySelector('link[rel="canonical"]');
        if (!linkCanonical) {
          linkCanonical = document.createElement('link');
          linkCanonical.setAttribute('rel', 'canonical');
          document.head.appendChild(linkCanonical);
        }
        linkCanonical.setAttribute('href', canonical_url);
      }
    } else if (transformedBlogPost?.title) {
       // Strip HTML tags just in case
       const plainTitle = transformedBlogPost.title.replace(/<[^>]*>?/gm, '');
       document.title = plainTitle;
    }
  }, [transformedBlogPost]);

  if (isLoading) {
    return (
      <div className="max-w-[90.69vw] sm:max-w-[90.80vw] lg:max-w-[89.58vw] mx-auto py-mob-40 sm:py-tab-40 lg:py-web-73">
        <div className="text-center py-web-100 flex justify-center">
          <Loader />
        </div>
      </div>
    );
  }

  if (error || !transformedBlogPost) {
    return (
      <div className="max-w-[90.69vw] sm:max-w-[90.80vw] lg:max-w-[89.58vw] mx-auto py-mob-40 sm:py-tab-40 lg:py-web-73">
        <div className="text-center py-web-100 flex flex-col items-center gap-4">
          <p className="text-web-24 text-red-500">Blog post not found.</p>
          <button
            onClick={() => window.history.back()}
            className="bg-SecondaryYellow/20 hover:bg-SecondaryYellow/30 text-TitleBlack px-6 py-2 rounded-lg transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (

    <div className="max-w-[90.69vw] sm:max-w-[90.80vw] lg:max-w-[89.58vw] mx-auto  py-mob-40 sm:py-tab-40 lg:py-web-73 font-satoshi ">

      <div className="max-w-[86vw] sm:max-w-[88vw] lg:max-w-[76vw] mx-auto">
        <div className="flex flex-col lg:flex-row gap-[6.25vw]">

          {/* Left Content */}
          <div className="lg:grow space-y-mob-28 sm:space-y-tab-35 lg:space-y-web-45">

            <div className='mb-mob-50 sm:mb-tab-50 lg:mb-0'>
              <div className="flex items-center justify-center gap-mob-10  sm:gap-tab-10 lg:gap-web-15 mt-mob-16 sm:mt-tab-16 lg:mt-web-10">
                <TagBadge label={transformedBlogPost.tag || 'new'} color="red" />
                <span className="block lg:text-web-20 text-[#757575] font-medium text-mob-14 sm:text-tab-16">
                  {transformedBlogPost.published_at}
                </span>
                <div className='flex gap-1'>

                  <span className="leading-none text-[#757575]" >·</span>
                  <span className="block lg:text-web-20 text-[#757575] capitalize text-mob-14 sm:text-tab-16">
                    {transformedBlogPost.content_type}
                  </span>
                </div>
              </div>

              <h1
                className="text-mob-30 sm:text-tab-40 lg:text-web-50 font-normal leading-[124%] text-TitleBlack group-hover:text-PrimaryLightBlue transition mt-mob-10 sm:mt-tab-15 lg:mt-web-10 text-center"
                dangerouslySetInnerHTML={{ __html: cleanHtmlClasses(transformedBlogPost.title) }}
              />
              <div className='relative w-full h-[65vw] sm:h-[45vw] lg:h-[28.59vw] my-mob-20 sm:my-tab-27 lg:my-web-33'>
                <Image
                  src={transformedBlogPost.image.blogImage}
                  alt={transformedBlogPost.image.alt || 'Blog Post Hero'}
                  fill
                  className="object-cover rounded-lg mx-auto"
                  priority
                  sizes="(max-width: 1024px) 100vw, 80vw"
                />
              </div>

            </div>

            {/* Article Content */}
            <article className="space-y-mob-28 sm:space-y-tab-38 lg:space-y-web-40">
              {/* At a Glance Section - Mobile/Tablet Only */}
              {headings.length > 0 && (
                <div className='lg:hidden bg-white border border-gray-500 rounded-lg p-mob-20 sm:p-tab-20 mb-mob-20 sm:mb-tab-20'>
                  <h3 className='text-mob-20 sm:text-tab-22 font-regular text-PrimaryLightBlue mb-mob-20 sm:mb-tab-20'>At a Glance</h3>
                  <ul className='space-y-mob-12 sm:space-y-tab-12'>
                    {headings.map((heading, index) => (
                      <li key={index} className='flex items-start gap-mob-8 sm:gap-tab-8'>
                        <div className='w-mob-12 sm:w-tab-12 h-mob-12 sm:h-tab-12 mt-[1.2326vw] lg:mt-[.2326vw] sm:mt-tab-1'>
                          <Image
                            src="/images/gtIcon.svg"
                            alt="bullet"
                            width={12}
                            height={12}
                            className="w-full h-full"
                          />
                        </div>
                        <span
                          className='text-TitleBlack text-mob-16 sm:text-tab-14 leading-0 cursor-pointer hover:text-PrimaryLightBlue transition-colors'
                          onClick={() => scrollToElement(heading.id)}
                        >
                          {heading.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex flex-col lg:flex-row ">
                {/* Left Content - Blog Content */}
                <div className="lg:w-[55%]">
                  <div
                    // className="blog-contents leading-4 sm:leading-6 lg:leading-6 text-TitleBlack text-mob-16 sm:text-tab-18 lg:text-web-20 prose prose-lg max-w-none [&_.blog-content]:mb-4 [&_.blog-content]:leading-relaxed [&_.blog-content]:text-TitleBlack [&_p]:mb-4 [&_p]:leading-relaxed [&_p]:text-justify [&_ul]:my-4 [&_ul]:pl-6 [&_ol]:my-4 [&_ol]:pl-6 [&_li]:my-2 [&_li]:leading-relaxed [&_blockquote]:my-6 [&_blockquote]:px-6 [&_blockquote]:py-4 [&_blockquote]:border-l-4 [&_blockquote]:border-PrimaryLightBlue [&_blockquote]:bg-gray-50 [&_blockquote]:italic [&_a]:text-PrimaryLightBlue [&_a]:underline [&_a]:transition-colors [&_a:hover]:text-blue-800 [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-lg [&_img]:my-4 [&_table]:w-full [&_table]:border-collapse [&_table]:my-4 [&_th]:p-3 [&_th]:border [&_th]:border-gray-200 [&_th]:text-left [&_th]:bg-gray-50 [&_th]:font-semibold [&_td]:p-3 [&_td]:border [&_td]:border-gray-200 [&_td]:text-left [&_h1]:leading-tight [&_h2]:leading-tight [&_h3]:leading-tight [&_h4]:leading-tight [&_h5]:leading-tight [&_h6]:leading-tight"
                    className="blog-contents leading-4 sm:leading-6 lg:leading-6 text-TitleBlack text-mob-16 sm:text-tab-18 lg:text-web-20 prose prose-lg max-w-none [&_.blog-content]:mb-4 [&_.blog-content]:leading-relaxed [&_.blog-content]:text-TitleBlack [&_p]:mb-4 [&_p]:leading-relaxed [&_p]:text-justify [&_ul]:my-4 [&_ul]:pl-6 [&_ol]:my-4 [&_ol]:pl-6 [&_li]:my-2 [&_li]:leading-relaxed [&_blockquote]:my-6 [&_blockquote]:px-6 [&_blockquote]:py-4 [&_blockquote]:border-l-4 [&_blockquote]:border-PrimaryLightBlue [&_blockquote]:bg-gray-50 [&_blockquote]:italic [&_a]:text-PrimaryLightBlue [&_a]:underline [&_a]:transition-colors [&_a:hover]:text-blue-800 [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-lg [&_img]:my-4 [&_table]:w-full [&_table]:border-collapse [&_table]:my-4 [&_th]:p-3 [&_th]:border [&_th]:border-gray-200 [&_th]:text-left [&_th]:bg-gray-50 [&_th]:font-semibold [&_td]:p-3 [&_td]:border [&_td]:border-gray-200 [&_td]:text-left [&_h1]:leading-tight [&_h2]:leading-tight [&_h3]:leading-tight [&_h4]:leading-tight [&_h5]:leading-tight [&_h6]:leading-tight [&_h3]:text-mob-21 [&_h3]:sm:text-tab-14 [&_h3]:lg:text-web-28 [&_h3]:text-PrimaryLightBlue [&_h3]:font-semibold"
                    dangerouslySetInnerHTML={{ __html: processedContent }}
                  />
                </div>

                {/* Right Content - Recent Blogs */}
                <div className="lg:w-[45%] lg:pl-[7.125vw]">
                  <div className="sticky top-3">
                    {/* At a Glance Section - Desktop Only */}
                    {headings.length > 0 && (
                      <div className='hidden lg:block bg-white border border-gray-500 rounded-lg p-web-20 mb-web-20 w-full'>
                        <h3 className='text-web-24 font-medium text-PrimaryLightBlue mb-web-20'>At A Glance</h3>
                        <ul className='space-y-web-12'>
                          {headings.map((heading, index) => (
                            <li key={index} className='flex items-start gap-web-8'>
                              <div className='w-web-12 h-web-12 mt-[0.445vw]'>
                                <Image
                                  src="/images/gtIcon.svg"
                                  alt="bullet"
                                  width={12}
                                  height={12}
                                  className="w-full h-full"
                                />
                              </div>
                              <span
                                className='text-TitleBlack text-web-19 leading-0 cursor-pointer hover:text-PrimaryLightBlue transition-colors'
                                onClick={() => scrollToElement(heading.id)}
                              >
                                {heading.text}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <RecentBlogs blogs={recentBlogs || []} title="Recent Blogs" />
                  </div>
                </div>
              </div>
            </article>
          </div>
        </div>
      </div>

      {/* Social Links */}
      <div className='flex items-center mt-mob-50 sm:mt-tab-50 lg:mt-web-50 gap-mob-25 sm:gap-tab-25 lg:gap-web-25 max-w-[86vw] sm:max-w-[88vw] lg:max-w-[76vw] mx-auto'>
        <div className="flex space-x-mob-22 sm:space-x-tab-22 lg:space-x-web-22 relative flex-shrink-0 ">
          <SocialLink href="" imgSrc="/images/fb-round.svg" alt="Facebook" />
          <SocialLink href="" imgSrc="/images/linkdln-round.svg" alt="LinkedIn" />
          <SocialLink href="" imgSrc="/images/insta-round.svg" alt="Instagram" />
          <SocialLink href="" imgSrc="/images/twiter-round.svg" alt="Twitter" />
        </div>
        <div className='grow h-[1px] bg-black/20' />
      </div>


    </div>

  );
}

function SocialLink({ href, imgSrc, alt }: { href: string; imgSrc: string; alt: string }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className='block w-mob-30 sm:w-tab-40 lg:w-web-45 h-mob-30 sm:h-tab-40  lg:h-web-45 relative'>
      <Image src={imgSrc} alt={alt} fill />
    </a>
  );
}